import React, {
  useRef,
  useEffect,
  useCallback,
  useState,
  forwardRef,
  useImperativeHandle,
} from "react";
import "./Scroller.css";
import { useScrollService } from "../../services/scrollService";

const Scroller = forwardRef(
  (
    {
      children,
      items = [],
      currentIndex = 0,
      onScroll,
      onIndexChange,
      sensitivity = 60,
      transitionDuration = 400,
      debounceDelay = 80,
      enableKeyboard = true,
      enableTouch = true,
      loop = false,
      direction = "vertical",
      className = "",
      momentum = true,
      preloadCount = 1,
      renderItem,
      ...props
    },
    ref
  ) => {
    const isScrollingRef = useRef(false);
    const [isTransitioning, setIsTransitioning] = useState(false);
    const [localCurrentIndex, setLocalCurrentIndex] = useState(currentIndex);

    // keep local index in sync with prop
    useEffect(() => {
      setLocalCurrentIndex(currentIndex);
    }, [currentIndex]);

    // index calculation logic
    const debouncedScroll = useCallback(
      (scrollDirection, force = 1) => {
        if (isScrollingRef.current) return;

        isScrollingRef.current = true;
        setIsTransitioning(true);

        let newIndex = localCurrentIndex;
        if (scrollDirection === "next") {
          newIndex =
            loop && localCurrentIndex === items.length - 1
              ? 0
              : Math.min(items.length - 1, localCurrentIndex + 1);
        } else if (scrollDirection === "prev") {
          newIndex =
            loop && localCurrentIndex === 0
              ? items.length - 1
              : Math.max(0, localCurrentIndex - 1);
        }

        if (newIndex !== localCurrentIndex) {
          setLocalCurrentIndex(newIndex);
          if (onIndexChange) onIndexChange(newIndex, scrollDirection);
        }

        if (onScroll) onScroll(scrollDirection, force, newIndex);

        setTimeout(() => {
          isScrollingRef.current = false;
          setIsTransitioning(false);
        }, transitionDuration);
      },
      [
        localCurrentIndex,
        items.length,
        loop,
        onIndexChange,
        onScroll,
        transitionDuration,
      ]
    );

    // use the shared scroll service
    const { containerRef, navigateTo } = useScrollService(debouncedScroll, {
      direction,
      sensitivity,
      transitionDuration,
      debounceDelay,
      momentum,
    });

    // public API methods
    const goToIndex = useCallback(
      (index) => {
        if (index >= 0 && index < items.length && index !== localCurrentIndex) {
          setLocalCurrentIndex(index);
          if (onIndexChange) {
            onIndexChange(index, index > localCurrentIndex ? "next" : "prev");
          }
        }
      },
      [items.length, localCurrentIndex, onIndexChange]
    );

    // Expose methods via ref
    useImperativeHandle(
      ref,
      () => ({
        scrollTo: navigateTo,
        goToIndex: goToIndex,
        getCurrentIndex: () => localCurrentIndex,
        getContainer: () => containerRef.current,
      }),
      [containerRef, navigateTo, goToIndex, localCurrentIndex]
    );

    // Also attach to DOM element for backward compatibility
    useEffect(() => {
      if (containerRef.current) {
        containerRef.current.scrollTo = navigateTo;
        containerRef.current.goToIndex = goToIndex;
        containerRef.current.getCurrentIndex = () => localCurrentIndex;
      }
    }, [navigateTo, goToIndex, localCurrentIndex, containerRef]);

    // compute visible items (preloading)
    const getVisibleItems = useCallback(() => {
      if (!items || items.length === 0) return [];
      const totalItems = items.length;
      const startIndex = Math.max(0, localCurrentIndex - preloadCount);
      const endIndex = Math.min(
        totalItems - 1,
        localCurrentIndex + preloadCount
      );

      const visibleItems = [];
      for (let i = startIndex; i <= endIndex; i++) {
        visibleItems.push({
          ...items[i],
          index: i,
          position:
            i === localCurrentIndex
              ? "current"
              : i < localCurrentIndex
              ? "previous"
              : "next",
          distance: Math.abs(i - localCurrentIndex),
        });
      }
      return visibleItems;
    }, [items, localCurrentIndex, preloadCount]);

    const visibleItems = getVisibleItems();

    return (
      <div
        ref={containerRef}
        className={`scroller ${direction} ${
          isTransitioning ? "transitioning" : ""
        } ${className}`}
        style={{
          "--transition-duration": `${transitionDuration}ms`,
          "--current-index": localCurrentIndex,
        }}
        {...props}
      >
        {/* render items if provided */}
        {items && items.length > 0 && renderItem ? (
          <div className="scroller-content">
            {visibleItems.map((item) =>
              renderItem(item, item.index, item.position)
            )}
          </div>
        ) : (
          children
        )}

        {/* indicators */}
        {items && items.length > 1 && (
          <div className="scroller-indicators">
            {items.map((_, index) => (
              <button
                key={index}
                className={`indicator ${
                  index === localCurrentIndex ? "active" : ""
                }`}
                onClick={() => goToIndex(index)}
                aria-label={`Go to item ${index + 1}`}
              />
            ))}
          </div>
        )}
      </div>
    );
  }
);

Scroller.displayName = "Scroller";

export default Scroller;
